// The search results page — route /search.
//
// GET /api/search (docs/API.md §3.8): every ticket, the imported history included, found
// by what its subject and messages say, best match first. An agent gets here from the
// top bar (web/src/layout/AppLayout.jsx): its search box, or, where the bar has no room
// for a box, its search icon. The page carries its own box as well, so a search can be
// started or changed here at any width.
//
// The search lives in the URL rather than in component state, as the queue's filters do
// (pages/tickets/List.jsx), so a results page is a link an agent can send and Back steps
// through searches. What the page decides rather than draws -- the summary line, where
// a result links, the search's address and its request -- is in search-results.js
// beside this file, where it is tested.

import { useEffect, useState } from 'react';
import {
  Link, useLocation, useNavigate, useSearchParams,
} from 'react-router';

import { api } from '../api/client.js';
import SearchForm from '../components/SearchForm.jsx';
import { StatusBadge } from '../components/tickets/Badges.jsx';
import { NOTE_BODY, NOTE_SURFACE, NoteChip } from '../components/tickets/MessageThread.jsx';
import { formatDate, formatDateTime } from '../components/tickets/format.js';
import {
  PAGE_SIZE, readSearch, resultHref, searchApiPath, searchHref, summarizeResults,
} from './search-results.js';

/**
 * The matching message's text: raw, so it is rendered as a text child and never as HTML
 * (docs/API.md §3.8).
 *
 * An internal note is drawn with the thread's own note tokens and "Internal note" chip,
 * imported from MessageThread.jsx (SPEC.md §7.4, RULE 3 in styles/app.css), decided
 * from `best_message_is_internal_note` and never from the text.
 * Anything else stays neutral: a result does not say whether its message came from the
 * customer or went to them, so it is drawn as neither.
 */
function Snippet({ text, isInternalNote }) {
  if (isInternalNote) {
    return (
      <div className={`mt-2 rounded-md border-l-4 px-3 py-2 ${NOTE_SURFACE}`}>
        <NoteChip />
        <p className={`mt-1 line-clamp-3 text-sm ${NOTE_BODY}`}>{text}</p>
      </div>
    );
  }
  return (
    <p className="mt-2 line-clamp-3 border-l-4 border-line-strong pl-3 text-sm text-ink-secondary">
      {text}
    </p>
  );
}

function ResultRow({ row }) {
  const updated = formatDate(row.updated_at);
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {/* One link per result, number and subject together, so a keyboard reaches each
            result in one stop rather than two. */}
        <Link
          to={resultHref(row)}
          title={row.subject}
          className="flex min-w-0 flex-1 items-baseline gap-3 text-ink hover:text-brand-700 dark:hover:text-brand-300"
        >
          <span className="shrink-0 font-mono text-xs text-ink-muted">#{row.public_ref}</span>
          <span className="truncate font-medium">{row.subject}</span>
        </Link>
        <StatusBadge status={row.status} namedStatus={row.named_status} />
      </div>
      <p className="mt-1 text-xs text-ink-muted">
        {row.organization_name && <>{row.organization_name} · </>}
        {updated && (
          <>
            Updated{' '}
            <time dateTime={row.updated_at} title={formatDateTime(row.updated_at) ?? undefined}>
              {updated}
            </time>
          </>
        )}
      </p>
      {row.snippet && (
        <Snippet text={row.snippet} isInternalNote={row.best_message_is_internal_note === true} />
      )}
    </li>
  );
}

function Panel({ children }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-12 text-center text-sm">
      {children}
    </div>
  );
}

export default function Search() {
  const [searchParams] = useSearchParams();
  // A new key for every navigation, the same address included: Enter on the search
  // already showing replaces the entry with one of the same path (searchSubmission), and
  // the key is what tells this page to fetch again.
  const { key } = useLocation();
  const navigate = useNavigate();
  const { q, offset } = readSearch(searchParams);
  // null when there is nothing to search for, so no request goes out at all.
  const path = q === '' ? null : searchApiPath({ q, offset });

  // Each result remembers the request it answers: its path, and the navigation that asked.
  // Until the response for THIS navigation arrives the page reads as loading, so one
  // search's results never sit under another's heading while the next request is in
  // flight, and a search run again visibly runs.
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (path === null) return undefined;
    const controller = new AbortController();
    api.get(path, { signal: controller.signal })
      .then((body) => {
        setResult({ path, key, rows: body?.data ?? [], page: body?.page ?? null, error: null });
      })
      .catch((error) => {
        if (controller.signal.aborted || error.name === 'AbortError') return;
        setResult({ path, key, rows: [], page: null, error });
      });
    return () => controller.abort();
  }, [path, key, attempt]);

  const current = path !== null && result?.path === path && result?.key === key ? result : null;
  const page = current?.page ?? null;
  const ready = current !== null && current.error === null;
  // The total is the envelope's, so it counts every match and not just this page.
  const summary = ready
    ? summarizeResults(current.rows, page?.total ?? 0, { offset: page?.offset ?? offset })
    : '';

  function retry() {
    setResult(null);
    setAttempt((count) => count + 1);
  }

  let body;
  if (path === null) {
    body = (
      <Panel>
        <p className="text-ink-secondary">
          Search every ticket, the imported history included, by its number or by the words
          in its subject and messages.
        </p>
      </Panel>
    );
  } else if (current === null) {
    body = <Panel><p className="text-ink-muted">Searching…</p></Panel>;
  } else if (current.error) {
    // The queue's error card (pages/tickets/List.jsx), which explains its red steps.
    body = (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
        <p className="text-sm font-medium text-red-800 dark:text-red-200">Could not search.</p>
        <p className="mt-1 text-sm text-red-700 dark:text-red-300">{current.error.message}</p>
        <button
          type="button"
          className="btn-ghost mt-3 ring-1 ring-red-300 dark:ring-red-800"
          onClick={retry}
        >
          Try again
        </button>
      </div>
    );
  } else if (current.rows.length === 0 && (page?.total ?? 0) > 0) {
    // A page past the end, reached by a stale link: the matches exist, so offer the way
    // back to them.
    body = (
      <Panel>
        <p className="text-ink-secondary">This page is past the last result.</p>
        <Link to={searchHref(q)} className="btn-primary mt-4">Back to the first page</Link>
      </Panel>
    );
  } else if (current.rows.length === 0) {
    body = (
      <Panel>
        <p className="text-ink">No tickets match “{q}”.</p>
        {/* The three rules in docs/API.md §3.8 that can make a search come back empty. */}
        <p className="mx-auto mt-2 max-w-prose text-ink-muted">
          Every word has to appear somewhere on the ticket. Words match from
          their start, so “termin” finds “terminal”, and very common words such as “the”
          are not searched.
        </p>
      </Panel>
    );
  } else {
    body = (
      <>
        <ol className="divide-y divide-line rounded-lg border border-line bg-surface">
          {current.rows.map((row) => <ResultRow key={row.ticket_id} row={row} />)}
        </ol>
        {page && (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              disabled={page.offset === 0}
              onClick={() => navigate(searchHref(q, Math.max(0, page.offset - PAGE_SIZE)))}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              disabled={!page.has_more}
              onClick={() => navigate(searchHref(q, page.offset + PAGE_SIZE))}
            >
              Next
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="space-y-4">
      {/* The page's own box, at every width: the top bar shows one only where it has
          room (layout/AppLayout.jsx). */}
      <SearchForm
        id="page-search"
        label="Search all tickets"
        className="flex max-w-2xl gap-2"
        inputClassName="min-w-0 flex-1"
      >
        <button type="submit" className="btn-primary h-9 shrink-0">Search</button>
      </SearchForm>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h1 className="min-w-0 truncate text-xl font-semibold tracking-tight" title={q || undefined}>
          {q === '' ? 'Search' : `Results for “${q}”`}
        </h1>
        {/* Always mounted, and a status region, so a screen reader hears the count each
            time a search lands rather than only when it happens to be read. */}
        <p role="status" className="text-sm text-ink-muted">{summary}</p>
      </div>
      {body}
    </div>
  );
}
